<template>
    <div class="flex">
        <div class="pl-2 py-2">
            <div
                class="flex w-10 h-10 overflow-hidden relative justify-center items-center rounded-full bg-gray-100 dark:bg-gray-600">
                        <span class="font-medium text-gray-600 dark:text-gray-300">
                            {{ comment.user.name.split(' ').filter((n) => n).slice(0, 2).map((n) => n[0]).join('') }}
                        </span>
            </div>
        </div>

        <div class="flex-1 px-3 py-2">
            <strong>{{ comment.user.email }}</strong>

            <span class="text-xs text-gray-400 ml-2">
                {{ dayjs(comment.created_at).format("DD-MM-YYYY HH:mm") }}
            </span>

            <p class="text-sm">{{ comment.body }}</p>

            <div class="pt-2" v-if="comment.media">
                <a v-for="attachment in comment.media"
                   class="inline-flex items-center w-full text-sm font-semibold transition-colors duration-150 hover:text-gray-800 dark:hover:text-gray-200"
                   :href="attachment.original_url"
                   target="_blank">
                    <div class="w-5 h-5">
                        <svg aria-hidden="true" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"></path>
                        </svg>
                    </div>
                    <span class="ml-2">{{ attachment.file_name }}</span>
                </a>
            </div>

            <template v-if="comment.children?.length">
                <h4 class="my-5 uppercase tracking-wide text-gray-400 font-bold text-xs">Replies</h4>

                <div class="space-y-4">
                    <Comment v-for="reply in comment.children" :comment="reply"
                             :level="level + 1" :ticket-id="ticketId"></Comment>
                </div>
            </template>

            <div class="mt-2" v-if="level < 2">
                <template v-if="showReplyForm">
                    <textarea id="message" rows="4" v-model="replyForm.body"
                              class="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                              placeholder="Your comment..."></textarea>
                    <div>
                        <jet-input-error :message="replyForm.errors.body" class="mt-2"/>
                    </div>
                    <input type="file"
                           multiple
                           class="hidden"
                           :ref="(el) => { fileInput = el }"
                           accept="image/*"
                           @change="updateAttachments">
                    <template v-if="replyForm.attachments?.length">
                        <div class="mt-2" v-for="attachment in replyForm.attachments">
                            {{ attachment.name }}
                        </div>
                    </template>
                    <jet-secondary-button class="mt-2 mr-2" type="button" @click.prevent="selectAttachments">
                        Select A New Attachment
                    </jet-secondary-button>
                    <jet-secondary-button type="button" class="mt-2"
                                          v-if="replyForm.attachments?.length" @click.prevent="deleteAttachments">
                        Remove Attachments
                    </jet-secondary-button>
                    <div class="mt-2">
                        <jet-button
                            @click.prevent="submit"
                            type="button"
                            class="mr-2 w-full px-5 py-3 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg sm:w-auto sm:px-4 sm:py-2 active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red"
                        >
                            Send
                        </jet-button>
                        <jet-button
                            @click.prevent="showReplyForm = false"
                            type="button"
                            class="w-full px-5 py-3 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg sm:w-auto sm:px-4 sm:py-2 active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red"
                        >
                            Cancel
                        </jet-button>
                    </div>
                </template>
                <template v-else>
                    <jet-button
                        @click.prevent="showReplyForm = true"
                        type="button"
                        class="w-full px-5 py-3 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-red-600 border border-transparent rounded-lg sm:w-auto sm:px-4 sm:py-2 active:bg-red-600 hover:bg-red-700 focus:outline-none focus:shadow-outline-red"
                    >
                        Reply
                    </jet-button>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import {defineComponent, ref} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import TablePagination from "@/Jetstream/TablePagination";
import TableLayout from "@/Jetstream/TableLayout";
import JetSelect from "@/Jetstream/Select";
import Pill from "@/Jetstream/Pill";
import JetButton from '@/Jetstream/Button';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetInputError from '@/Jetstream/InputError';
import {useForm} from "@inertiajs/inertia-vue3";
import dayjs from "dayjs";

export default defineComponent({
    methods: {dayjs},
    components: {
        Pill,
        TableLayout,
        TablePagination,
        AppLayout,
        JetSelect,
        JetButton,
        JetSecondaryButton,
        JetInputError,
    },
    props: {
        ticketId: {
            type: Number,
            required: true,
        },
        parentId: {
            type: Number,
            default: null,
        },
        level: {
            type: Number,
            default: 1,
        },
        comment: {
            type: Object,
            required: true,
        },
    },
    setup(props) {
        const showReplyForm = ref(false);
        const fileInput = ref(null);

        const replyForm = useForm({
            body: '',
            parent_id: props.comment.id,
            attachments: [],
        });

        const submit = () => {
            replyForm.post(route('admin.ticket.comment.store', props.ticketId), {
                onSuccess: () => replyForm.reset(),
            });
        };

        const deleteAttachments = () => {
            replyForm.attachments = [];
        };

        const selectAttachments = () => {
            fileInput.value.click();
        };

        const updateAttachments = (event) => {
            replyForm.attachments = event.target.files;
        };

        return {
            fileInput,
            replyForm,
            showReplyForm,
            submit,
            deleteAttachments,
            selectAttachments,
            updateAttachments,
        };
    }
})
</script>
